import {useState} from 'react';

import {
  FileImageVariant,
  FileType,
  useCreateFileMutation,
  useUpdateUserAvatar,
} from '#apollo/schema';
import {Avatar, Icon, Link, Text} from '#component/base';
import {useToastQueue} from '#hook';
import {uploadFile} from '#service/file/upload';
import {sprinkles} from '#style/sprinkles';

import {AvatarImageInputProps} from './AvatarImageInput.types';

import * as css from './AvatarImageInput.css';

const VARIANT = FileImageVariant.Size_160x160;

export const AvatarImageInput = ({image, text}: AvatarImageInputProps) => {
  const [loading, setLoading] = useState(false);
  const [createFile] = useCreateFileMutation();
  const [updateUserAvatar] = useUpdateUserAvatar();
  const {addToast} = useToastQueue();

  const handleChangeAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setLoading(true);

      try {
        const {data} = await createFile({
          variables: {input: {contentType: file.type, name: file.name, type: FileType.Image}},
        });
        const {id, uploadUrl} = data?.createFile || {};

        if (id && uploadUrl) {
          const uploadSuccess = await uploadFile({file, url: uploadUrl});

          if (uploadSuccess) {
            await updateUserAvatar({variables: {input: {avatarFileId: id}, imageVariant: VARIANT}});
          }
        }

        addToast({children: 'Avatar updated'});
        setLoading(false);
      } catch (e) {
        console.warn('Error', e);
        addToast({children: 'Unable to change avatar! Please contact support.'});
        setLoading(false);
      }
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await updateUserAvatar({variables: {input: {avatarFileId: null}, imageVariant: VARIANT}});
      addToast({children: 'Avatar updated'});
    } catch (e) {
      console.warn('Error', e);
      addToast({children: 'Unable to update avatar! Please contact support.'});
    }
  };

  return (
    <div
      className={sprinkles({
        flex: 'inline',
        direction: ['col', 'row'],
        gap: [16, 32],
        placeContent: 'center',
        placeItems: 'center',
      })}>
      <label
        className={sprinkles({
          direction: 'col',
          flex: 'flex',
          placeContent: 'center',
          placeItems: 'center',
          position: 'relative',
        })}>
        <Avatar image={image} size="medium" text={text} />

        <div className={css.mobileInput}>
          <Icon color="burgundy" name="Camera" weight="bold" />
        </div>

        <input
          aria-label="Change profile photo"
          className={css.input}
          disabled={loading}
          onChange={handleChangeAvatar}
          type="file"
          accept="image/*"
        />
      </label>

      <Text color="doveGray" className={sprinkles({flex: 'flex', direction: 'col', gap: 12})}>
        <Text as="label" className={css.desktopInput}>
          <input
            className={css.input}
            disabled={loading}
            onChange={handleChangeAvatar}
            type="file"
            accept="image/*"
          />

          <Link as="span" disabled={loading}>
            <Text color="black">Change profile photo</Text>
          </Link>
        </Text>

        {!!image && (
          <Link as="button" disabled={loading} onClick={handleRemoveAvatar}>
            Remove photo
          </Link>
        )}
      </Text>
    </div>
  );
};
